import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Spin } from 'antd'
import UnauthorizedScreen from './UnauthorizedScreen.jsx'
import { useGlobalConfigsPoll } from '../../app_modules/global_configs/hooks/useGlobalConfigsPoll.ts'

/**
 * @param {{ children: React.ReactNode, fallback?: React.ReactNode }} props
 *   `fallback` replaces the default loading screen; most routes leave it out.
 */
export function AuthGuard({ children, fallback }) {
  const { isAuthenticated, loading, appInitialized, isLoadingEssentialData, isUnauthorized } = useSelector(
    (state) => state.auth
  )
  const navigate = useNavigate()

  // Keeps this session's cfg copy current without a reload. Mounted here so it
  // covers the whole authenticated app and nothing else; it no-ops until the
  // user is authenticated. Called before the early returns below — hooks must
  // run unconditionally.
  useGlobalConfigsPoll()

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login')
    }
  }, [isAuthenticated, loading, navigate])

  // Show loading if auth is loading OR if user is authenticated but essential data is not ready
  const isAppLoading = loading || (isAuthenticated && (!appInitialized || isLoadingEssentialData))

  if (isAppLoading) {
    let loadingMessage = 'Checking authentication...'

    if (isAuthenticated && (!appInitialized || isLoadingEssentialData)) {
      loadingMessage = 'Initializing portal...'
    }

    return (
      fallback || (
        <div className='min-h-dvh bg-gray-50 flex items-center justify-center'>
          <div className='text-center max-w-md w-full px-8'>
            <Spin size='large' />
            <p className='text-gray-600 mt-4'>{loadingMessage}</p>
          </div>
        </div>
      )
    )
  }

  if (!isAuthenticated) {
    return null // Let the useEffect handle the redirect
  }

  // Show unauthorized screen if user is not authorized
  if (isUnauthorized) {
    return <UnauthorizedScreen />
  }

  return <>{children}</>
}

// Hook for checking authentication status
function useAuth() {
  const authState = useSelector((state) => state.auth)
  const { isAuthenticated, user, loading, appInitialized, isLoadingEssentialData, isUnauthorized } = authState

  return {
    isAuthenticated,
    user,
    loading,
    appInitialized,
    isLoadingEssentialData,
    isUnauthorized,
    isLoggedIn: isAuthenticated && user !== null,
    isAppReady: isAuthenticated && appInitialized && !isLoadingEssentialData && !isUnauthorized,
    authState // Expose full auth state for utility functions
  }
}
